import { Field, getIn, useFormikContext } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { Col, Row, Spinner, Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import camelCaseRecursive from 'camelcase-keys-recursive';
import { FormikCheckBox, FormikDatePicker, FormikInput, FormikSelect, SimpleCenteredModal } from '../../../components';
import { indexTerminationReasonsRequest } from '../../../requests/terminationReasons';
import AvancedFilerInput from './Form/AvancedFilterInput';
import DataTable from './Form/DataTable';
import { indexEmployeesRequest } from '../../../requests/employees';
import { payrollSettlementRequest } from '../../../requests/payrollSettlements';
import useTerminationReasonHook from '../hooks/useTerminationReasonHook';
import { handleDateDiff } from '../helpers';

const singleOptions = [
  { label: 'Si', value: 'true' },
  { label: 'No', value: 'false' }
];

const SettlementGroupForm = ({ history }) => {
  const { values, errors, touched, setFieldValue, setFieldTouched, submitForm, isSubmitting } = useFormikContext();
  const [terminationReasons, setTerminationReasons] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const { currentCompany } = useSelector(state => state.auth);
  const causalJustification = getIn(values, 'settlementGroup[causalJustification]');
  const employees = getIn(values, 'settlementGroup[employees]') || [];
  const selectedTerminationReason = getIn(values, 'settlementGroup.terminationReason');
  const { noticeMonthDisabled } = useTerminationReasonHook(values.settlementGroup);
  const dispatch = useDispatch();

  const handleSuccessTermination = ({ data }) => {
    setTerminationReasons(data.data);
  };

  const fetchTerminationReasons = useCallback(() => {
    indexTerminationReasonsRequest({
      dispatch,
      params: {
        paginate: false
      },
      successCallback: handleSuccessTermination,
      callback: () => {}
    });
  }, [dispatch]);

  const fetchEmployees = async params => {
    setShowSpinner(true);
    indexEmployeesRequest({
      params: {
        ...params,
        is_dt: false,
        sort_column: 'name',
        paginate: false,
        settlement: true
      },
      dispatch,
      successCallback: response => {
        setShowSpinner(false);
        setFieldValue('employeesToDestroy', values.settlementGroup.employees); /* si existen empleados los elimina */
        const employeesData = response.data.data;
        const fullEmployees = employeesData.map(employee => {
          return {
            ...employee,
            workedTime: '',
            contractType: employee.parsed_contract_type,
            startDate: moment(employee.start_date, 'YYYY-MM-DD').format('DD/MM/YYYY')
          };
        });
        setFieldValue('settlementGroup[employees]', camelCaseRecursive(fullEmployees));
        setFieldValue(
          'settlementGroup[employeeIds]',
          response.data.data.map(item => item.id)
        );
      },
      callback: () => setShowSpinner(false)
    });
  };

  const fetchPayrollData = formEndDate => {
    if (!formEndDate || !values?.settlementGroup?.employeeIds) return;
    const dateToSearch = formEndDate;
    payrollSettlementRequest({
      dispatch,
      params: {
        filter_by_company: currentCompany.id,
        filter_employees: [...values?.settlementGroup?.employeeIds],
        month_date: dateToSearch,
        year_date: dateToSearch,
        settlement_data: true
      },
      successCallback: ({ data }) => {
        const convertedData = camelCaseRecursive(data);
        const { employeeData } = convertedData;
        setFieldValue('settlementGroup[ufValue]', employeeData.ufValue);
        const ids = [];
        const newEmployeeData = values.settlementGroup.employees.map(employee => {
          ids.push(employee.id);
          const vacationData = employeeData.vacationData.find(vacData => vacData.employeeId === employee.id);
          return {
            ...employee,
            workedTime: handleDateDiff(employee.startDate, moment(formEndDate, 'DD-MM-YYYY').format('DD/MM/YYYY')),
            proportionalVacationDays: vacationData?.proportionalVacation || 0,
            legalVacationDays: vacationData?.legalVacation || 0,
            aditionalVacationDays: vacationData?.additionalVacation || 0,
            progressiveVacationDays: vacationData?.progressiveVacation || 0
          };
        });
        setFieldValue('settlementGroup[employees]', newEmployeeData);
      }
    });
  };

  useEffect(() => {
    fetchTerminationReasons();
  }, [fetchTerminationReasons]);

  useEffect(() => {
    if (causalJustification) {
      setModalShow(true);
    }
  }, [causalJustification]);

  useEffect(() => {
    if (terminationReasons.length === 0) return;
    if (!selectedTerminationReason) return;
    const selectedReason = terminationReasons.find(reason => reason.value === selectedTerminationReason);
    if (!selectedReason) return;
    if (selectedReason !== values.settlementGroup.terminationReasonObject) {
      setFieldValue('settlementGroup[noticeMonth]', 'false');
      setFieldValue('settlementGroup[terminationReasonObject]', selectedReason || {});
    }
  }, [selectedTerminationReason, setFieldValue, terminationReasons, values.settlementGroup.terminationReasonObject]);
  return (
    <Col>
      <Row>
        <Col xl={6} md={6} xs={12}>
          <Field name="settlementGroup[name]">
            {({ field }) => (
              <FormikInput
                {...field}
                label="Nombre"
                value={values?.settlementGroup?.name}
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col xl={2} md={2} lg={2} xs={12} className="settlement_settings__date-picker">
          <Field name="settlementGroup[issueDate]">
            {({ field }) => (
              <FormikDatePicker
                {...field}
                isOutsideRange={() => false}
                label="Fecha de Emision"
                placeholder="dd/mm/aaaa"
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col xl={2} md={2} lg={2} xs={12} className="settlement_settings__date-picker">
          <Field name="settlementGroup[endDate]">
            {({ field }) => (
              <FormikDatePicker
                {...field}
                isOutsideRange={() => false}
                label="Fecha de Termino"
                placeholder="dd/mm/aaaa"
                onDateChange={date => {
                  setFieldValue(field.name, moment.isMoment(date) ? date.format('DD/MM/YYYY') : '');
                  fetchPayrollData(date.format('DD/MM/YYYY'));
                }}
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col xl={2} md={2} lg={2} xs={12}>
          <Field name="settlementGroup[ufValue]">
            {({ field }) => (
              <FormikInput
                {...field}
                label="Valor UF"
                placeholder=""
                leftAddon="$"
                value={new Intl.NumberFormat('es-CL', {
                  maximumFractionDigits: 2
                }).format(values?.settlementGroup?.ufValue)}
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
                readOnly
              />
            )}
          </Field>
        </Col>
      </Row>
      <Row>
        <AvancedFilerInput fetchEmployees={fetchEmployees} />
        <Col xl={6} xs={12} md={6} lg={6}>
          <Field name="settlementGroup[terminationReason]">
            {({ field }) => (
              <FormikSelect
                {...field}
                abbr
                label="Causal de Término Contrato"
                placeholder="Causal de Término Contrato"
                options={terminationReasons}
                value={values?.settlementGroup?.terminationReason}
                defaultValue={values?.settlementGroup?.terminationReason}
                onChange={data => {
                  setFieldValue(field.name, data ? data.value : '');
                  const selectedReason = terminationReasons.find(reason => reason.value === data.value);
                  setFieldValue('terminationReasonObject', selectedReason || {});
                }}
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
      </Row>
      <Row>
        <Col xl={3} xs={12} md={4} lg={3}>
          <Field name="settlementGroup[simulation]">
            {({ field }) => (
              <FormikSelect
                {...field}
                abbr
                label="Simulación"
                placeholder="Si"
                options={singleOptions}
                value={values?.settlementGroup.simulation}
                defaultValue={values?.settlementGroup?.simulation}
                onChange={data => setFieldValue(field.name, data ? data.value : '')}
                setFieldTouched={() => setFieldTouched(field.name)}
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col xl={4} xs={12} md={4} lg={4}>
          <Field name="settlementGroup[noticeMonth]">
            {({ field }) => (
              <FormikSelect
                {...field}
                abbr
                label="Mes de Aviso"
                placeholder="Si"
                options={singleOptions}
                value={values?.settlementGroup.noticeMonth}
                defaultValue={values?.settlementGroup?.noticeMonth}
                onChange={data => setFieldValue(field.name, data ? data.value : '')}
                setFieldTouched={() => setFieldTouched(field.name)}
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
                isDisabled={noticeMonthDisabled}
              />
            )}
          </Field>
        </Col>
        <Col md={3}>
          <Field name="settlementGroup[causalJustification]">
            {({ field }) => (
              <FormikCheckBox
                {...field}
                field={field}
                label="Justificacion de la Causal"
                custom
                type="switch"
                className="settlement_form__checkbox"
              />
            )}
          </Field>
        </Col>
      </Row>
      <Row>
        {showSpinner ? (
          <Col className="div-content mt-5 mb-5">
            <div className="containerSpinnerLoadDash" style={{ position: 'absolute', height: '100%' }}>
              <Spinner animation="border" variant="primary" />
            </div>
          </Col>
        ) : (
          <Col className="mt-1">
            <DataTable employees={employees} />
          </Col>
        )}
      </Row>

      <Row className="d-flex justify-content-end my-3">
        <Col md={2} xl={2} lg={2} xs={12} className="pb-2">
          <Button
            block
            variant="outline-info"
            disabled={isSubmitting}
            onClick={() => {
              history.push('/settlement_simulator');
            }}
          >
            Cancelar
          </Button>
        </Col>
        <Col md={2} xl={2} lg={2} xs={12}>
          <Button block variant="primary" disabled={isSubmitting} onClick={() => submitForm()}>
            Continuar
          </Button>
        </Col>
      </Row>

      <SimpleCenteredModal
        title="Justificación de la Causal de Término"
        body={
          <Row>
            <Col>
              <Field name="settlementGroup[justificationTerminationReason]">
                {({ field }) => (
                  <FormikInput
                    {...field}
                    label="Descripcion"
                    value={values.settlementGroup?.justificationTerminationReason}
                    error={getIn(errors, field.name)}
                    touched={getIn(touched, field.name)}
                    as="textarea"
                  />
                )}
              </Field>
            </Col>
          </Row>
        }
        show={modalShow}
        onHide={() => setModalShow(false)}
      />
    </Col>
  );
};

export default SettlementGroupForm;
